import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PostCard from '../components/PostCard';
import './Profile.css';

function Profile() {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const isOwnProfile = userId === currentUser.userId.toString();

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`/api/users/${userId}`);
      setProfile(response.data);
      setUserDisplayName(response.data.username || '');
      setProfileImageUrl(response.data.profileImageUrl || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchUserPosts = useCallback(async () => {
    try {
      const response = await axios.get(`/api/posts/user/${userId}`);
      setPosts(response.data.filter(post => post.postType === 'ARTICLE'));
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  }, [userId]);

  const checkFollowStatus = useCallback(async () => {
    try {
      const response = await axios.get(`/api/follow/user/${userId}/is-following`);
      setIsFollowing(response.data);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    fetchProfile();
    fetchUserPosts();
    if (!isOwnProfile) {
      checkFollowStatus();
    }
  }, [userId, isOwnProfile, fetchProfile, fetchUserPosts, checkFollowStatus]);

  const handleFollow = async () => {
    try {
      await axios.post(`/api/follow/user/${userId}`);
      const statusResponse = await axios.get(`/api/follow/user/${userId}/is-following`);
      setIsFollowing(statusResponse.data);
      fetchProfile();
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert(error.response?.data?.message || 'Failed to update follow status');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put('/api/users/me', {
        userDisplayName: userDisplayName.trim(),
        profileImageUrl
      });
      setProfile(response.data);
      setEditing(false);
      setSaveMessage('Profile updated! +5 points');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handlePostDeleted = (postId) => {
    setPosts(posts.filter(post => post.id !== postId));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!profile) {
    return <div className="error-message">Profile not found</div>;
  }

  const displayName = profile.username || 'User';

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-avatar">
          {profile.profileImageUrl ? (
            <img src={profile.profileImageUrl} alt={displayName} className="profile-avatar-img" />
          ) : (
            displayName.charAt(0).toUpperCase()
          )}
        </div>

        <div className="profile-info">
          <h1>{displayName}</h1>
          {profile.isAnonymous && <p className="profile-email">Guest account</p>}

          {editing ? (
            <form className="profile-edit-form" onSubmit={handleSaveProfile}>
              <input
                type="text"
                value={userDisplayName}
                onChange={(e) => setUserDisplayName(e.target.value)}
                placeholder="Display name"
                maxLength={100}
                required
              />
              <input
                type="url"
                value={profileImageUrl}
                onChange={(e) => setProfileImageUrl(e.target.value)}
                placeholder="Profile image URL"
              />
              <div className="profile-edit-actions">
                <button type="submit" className="follow-btn">Save (+5 pts)</button>
                <button type="button" className="follow-btn following" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {saveMessage && <p className="profile-save-message">{saveMessage}</p>}
            </>
          )}

          <div className="profile-stats">
            <div className="stat-item">
              <span className="stat-value">{profile.postsCount}</span>
              <span className="stat-label">Articles</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{profile.followersCount}</span>
              <span className="stat-label">Followers</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{profile.followingCount}</span>
              <span className="stat-label">Following</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{profile.totalPoints}</span>
              <span className="stat-label">Points</span>
            </div>
          </div>

          {isOwnProfile && !editing && !profile.isAnonymous && (
            <button className="follow-btn" onClick={() => setEditing(true)}>
              Edit Profile
            </button>
          )}

          {!isOwnProfile && (
            <button
              className={`follow-btn ${isFollowing ? 'following' : ''}`}
              onClick={handleFollow}
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </button>
          )}
        </div>
      </div>

      <div className="profile-posts">
        <h2>Articles</h2>
        {posts.length === 0 ? (
          <p className="no-posts">No articles yet</p>
        ) : (
          posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onDelete={handlePostDeleted}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default Profile;
